import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, switchMap, take, tap, map } from 'rxjs/operators';
import { LocalStorageService } from '../../common/services/local-storage/local-storage.service';
import { LocationPreCachingService } from '../../portal/services/location-pre-caching.service';
import { BroadcastService } from '../../common/services/broadcast/broadcast.service';
import { CommonEvents } from '../../common/enums/common-events.enum';
import { IInsertToCacheEvent } from '../../common/interfaces/iinsert-to-cache-event';
import { CachingService } from '../../common/services/caching/caching.service';
import { PortalEvents } from '../../portal/enums/portal-events.enum';
import { Claims } from '../../common/types/claims';
import { UserHttpService } from './user-http.service';
import { IAggregateLocationViewModel } from '../models/iaggregate-location-view-model';
import { LocationService } from '../../location/services/location.service';
import { UserEvents } from '../enums/user-events.enum';
import { LocationChange } from '../types/location-change';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    public get activeLocation(): Observable<IAggregateLocationViewModel> {
        return this._activeLocation.pipe(
            tap(locationId => this.lastActiveLocation = locationId),
            switchMap(locationId => this.getLocation(locationId))
        );
    }

    public cacheId = btoa('userLocations');

    public get claims(): Claims {
        if (!this._claims) {
            this._claims = JSON.parse(this.localStorageService.get('claims')) as Claims;
        }
        return this._claims;
    }

    public set claims(claims: Claims) {
        this._claims = claims;
        if (claims) {
            this.localStorageService.set('claims', JSON.stringify(claims));
        } else {
            this.localStorageService.remove('claims');
        }
    }

    private _claims?: Claims;
    private locations = new Map<number, IAggregateLocationViewModel>();
    private readonly _key = 'activeLocationId';
    private _activeLocation: Subject<number> = new ReplaySubject<number>(1);
    private lastActiveLocation: number;

    constructor(
        private localStorageService: LocalStorageService,
        private httpService: UserHttpService,
        private router: Router,
        private locationPreCachingService: LocationPreCachingService,
        private locationService: LocationService,
        private broadcastService: BroadcastService,
        private cachingService: CachingService
    ) {
        cachingService
            .get<number>(this._key)
            .subscribe(
                location => {
                    if (location) {
                        this._activeLocation.next(location);
                    }
                }
            );
    }

    /**
     * Inits user and his/her locations
     * @param username username string
     */
    public initUserInfo(claims: Claims) {
        this.claims = claims;

        this.getUserLastLoadedLocation()
            .pipe(
                take(1),
                catchError(() => {
                    return of(0);
                }),
            )
            .subscribe(locationId => {
                if (locationId !== 0) {
                    this.fetchUserDefaultLocation(locationId, claims.sub);
                }
            });
    }

    public updateLastLoadedLocation(locationId: number) {
        if (locationId === this.lastActiveLocation) {
            return;
        }

        const locationChange: LocationChange = { oldLocationId: this.lastActiveLocation, newLocationId: locationId };
        this.broadcastService.broadcast(UserEvents.ActiveLocationChanged, locationChange);

        this.setActiveLocation(locationId);

        return this.postUserlastLoadedLocation({
            entity: { locationId }
        }).subscribe();
    }

    public updateUserpreferences( Theme: string) {
        return this.postUserpreferences({ Theme }
        ).subscribe();
    }

    /**
     * Fetches all user locations and precaches all non-default locations
     * @param defaultLocationId - default location that is being loaded after login
     * @param username name of current user
     */
    private fetchUserDefaultLocation(defaultLocationId: number, username: string) {
        this.locationService.getAggregateLocation(defaultLocationId)
            .subscribe(result => {
                if (result) {
                    this.activeLocationFromCache.pipe(take(1)).subscribe(id => {
                        if (!id) {
                            this.setActiveLocation(result.id);

                            this.locationPreCachingService.preCacheLocation([result.id], username);

                            const cache = [
                                {
                                    id: result.id,
                                    description: result.description
                                }
                            ];

                            this.broadcastService.broadcast(CommonEvents.InsertToCache, {
                                cacheId: this.cacheId,
                                body: cache
                            } as IInsertToCacheEvent);
                        }
                    });
                }
            });
    }

    public getLastActiveLocation(): Observable<IAggregateLocationViewModel> {
        const locationFromCache$ = this.getLastActiveLocationFromCache();
        const locationFromAPI$ = this.getLastActiveLocationFromAPI();

        return this.getLocationDataSource(locationFromAPI$, locationFromCache$);
    }

    private get activeLocationFromCache() {
        return this.cachingService.get<number>(this._key);
    }

    private getLastActiveLocationFromCache(): Observable<null | IAggregateLocationViewModel> {
        return this.activeLocationFromCache
            .pipe(
                switchMap(locationId => {
                    if (!locationId) {
                        return of(null);
                    }
                    return this.getLocation(locationId);
                })
        );
    }

    private getLastActiveLocationFromAPI(): Observable<IAggregateLocationViewModel> {
        return this.getUserLastLoadedLocation()
            .pipe(
                take(1),
                catchError(() => {
                    return of(0);
                }),
                switchMap(id => {
                    if (id === 0) {
                        return of(null);
                    }
                    return this.locationService.getAggregateLocation(id)
                        .pipe(
                            take(1),
                            catchError(() => {
                                return of(null);
                            }),
                        );
                }));
    }

    private getLocationDataSource(locationFromAPI$, locationFromCache$): Observable<IAggregateLocationViewModel> {
        return locationFromCache$.pipe(
            switchMap(location => {
                return location ? of(location) : locationFromAPI$;
            }));
    }

    public getLocationsByIds(locationIds: number[]) {
        const locations$: Observable<IAggregateLocationViewModel | HttpErrorResponse>[] = [];

        if (locationIds.length === 0) {
            return of([]);
        }

        for (const locationId of locationIds) {
            locations$.push(
                this.locationService
                    .getAggregateLocation(locationId)
                    .pipe(
                        catchError(error => of(error)),
                    ));
        }

        return forkJoin(locations$)
            .pipe(
                map(results => {
                    return results.filter(result => !this.isHttpErrorResponse(result));
                }),
            );
    }

    public changeLocation(locationId: number) {
        this.updateLastLoadedLocation(locationId);
        this.activeLocation
            .pipe(take(1))
            .subscribe(
                location => this.broadcastService.broadcast(PortalEvents.locationSearch, location)
            );

        this.router.navigateByUrl(`places/${locationId}`);
    }

    public getLocation(id: number): Observable<IAggregateLocationViewModel> {
        const location$ = new Observable<IAggregateLocationViewModel>(observer => {
            observer.next(this.locations.get(id));
            observer.complete();
        });

        if (!this.locations.has(id)) {
            return this.locationService.getAggregateLocation(id)
                .pipe(
                    catchError(() => {
                        return throwError(`Invalid location: ${id}`);
                    }),
                    switchMap(
                        (location: IAggregateLocationViewModel) => {
                            this.locations.set(location.id, location);
                            return location$;
                    }));
        } else {
            return location$;
        }
    }

    public setActiveLocation(locationId: number) {
        this.cachingService.put(this._key, locationId);
        this._activeLocation.next(locationId);
    }

    public getUserLastLoadedLocation(): Observable<number> {
        return this.httpService.get<number>('user/lastLoadedLocation');
    }

    public postUserlastLoadedLocation(loc: { entity: { locationId: number } }): Observable<any> {
        return this.httpService.post<any>('user/lastLoadedLocation', loc);
    }

    public getUserpreferences(): Observable<any> {
        return this.httpService.get<any>('user/preferences');
    }

    public postUserpreferences(pref: { Theme: string } ): Observable<any> {
        return this.httpService.post<any>('user/preferences', pref);
    }

    private isHttpErrorResponse(obj: IAggregateLocationViewModel | HttpErrorResponse): obj is HttpErrorResponse {
        return obj instanceof HttpErrorResponse;
    }
}
